import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";
import { getAppleIdToken, getGoogleAccessToken, getMicrosoftIdToken } from "lib/socialAuth";
import { useMobileLocale } from "mobile/i18n";

import logo from "assets/images/logos/ShortLogo.png";

const COLORS = {
  ink: "#0b1118",
  line: "rgba(11,17,24,0.1)",
  red: "#cf241f",
};

function MicrosoftMark() {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 7px)", gap: "2px" }}>
      <Box sx={{ width: 7, height: 7, bgcolor: "#F25022" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#7FBA00" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#00A4EF" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#FFB900" }} />
    </Box>
  );
}

const resolveRedirectUriForCurrentHost = (configured, fallbackPath) => {
  const fallback = `${window.location.origin}${fallbackPath}`;
  const value = String(configured || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.host !== window.location.host) return `${window.location.origin}${parsed.pathname || fallbackPath}`;
    return parsed.toString();
  } catch {
    return fallback;
  }
};

export default function SignInMobile() {
  const storefront = getStorefrontConfig();
  const { t } = useMobileLocale();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithSocial, token } = useAuth();

  const queryNext = String(searchParams.get("next") || "").trim();
  const isSafeNext =
    queryNext.startsWith("/m/") ||
    queryNext.startsWith("/register/valve-body/");
  const redirectTo = isSafeNext ? queryNext : "/m/account";
  const registerPath = useMemo(() => {
    const next = isSafeNext ? queryNext : "/m/account";
    const params = new URLSearchParams({ next, source: "mobile-sign-in" });
    return `/m/register?${params.toString()}`;
  }, [isSafeNext, queryNext]);

  const googleClientId = String(
    process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || ""
  ).trim();
  const appleClientId = String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
  const microsoftClientId = String(process.env.REACT_APP_MICROSOFT_CLIENT_ID || "").trim();
  const microsoftTenantId = String(process.env.REACT_APP_MICROSOFT_TENANT_ID || "common").trim() || "common";
  const appleRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_APPLE_REDIRECT_URI,
    "/authentication/sign-in"
  );
  const microsoftRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    "/msal-popup.html"
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const from = queryNext.startsWith("/") ? queryNext : location.state?.from?.pathname;
    if (typeof from === "string" && from && !from.includes("/authentication/")) {
      try {
        sessionStorage.setItem("postLoginNext", from);
      } catch {
        // ignore storage errors
      }
    }
  }, [location.state, queryNext]);

  useEffect(() => {
    if (token) navigate(redirectTo, { replace: true });
  }, [token, redirectTo, navigate]);

  const finishLogin = (ok) => {
    if (ok) navigate(redirectTo, { replace: true });
    else setError(t("Invalid credentials."));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!String(email || "").trim() || !password) {
      setError(t("Enter your email and password."));
      return;
    }
    try {
      setLoading(true);
      setError("");
      finishLogin(await login(String(email || "").trim(), password));
    } catch {
      setError(t("Login failed."));
    } finally {
      setLoading(false);
    }
  };

  const onSocial = async (provider) => {
    const normalized = String(provider || "").trim().toLowerCase();
    const enabled =
      (normalized === "google" && !!googleClientId) ||
      (normalized === "apple" && !!appleClientId) ||
      (normalized === "microsoft" && !!microsoftClientId);
    if (!enabled) {
      setError(`${normalized[0].toUpperCase()}${normalized.slice(1)} sign in is not configured yet.`);
      return;
    }
    try {
      setLoading(true);
      setError("");
      let payload = {};
      if (normalized === "google") payload = await getGoogleAccessToken({ clientId: googleClientId });
      if (normalized === "apple") payload = await getAppleIdToken({ clientId: appleClientId, redirectURI: appleRedirectUri });
      if (normalized === "microsoft") {
        payload = await getMicrosoftIdToken({
          clientId: microsoftClientId,
          tenantId: microsoftTenantId,
          redirectUri: microsoftRedirectUri,
        });
      }
      const ok = await loginWithSocial({
        provider: normalized,
        ...payload,
        companyKey: storefront?.companyKey || undefined,
      });
      finishLogin(ok);
    } catch (err) {
      setError(err?.message || t("Social sign in failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#fbfbfa", color: COLORS.ink }}>
      <Container
        maxWidth="sm"
        sx={{
          px: 2,
          pt: "calc(env(safe-area-inset-top) + 16px)",
          pb: 5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 1,
            minHeight: 54,
            bgcolor: "#fff",
            "& fieldset": { borderColor: "rgba(11,17,24,0.12)" },
            "&:hover fieldset": { borderColor: "rgba(207,36,31,0.35)" },
            "&.Mui-focused fieldset": { borderColor: COLORS.red },
          },
          "& .MuiInputLabel-root.Mui-focused": { color: COLORS.red },
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3.5 }}>
          <MDBox
            component="img"
            src={logo}
            alt="vbspecialties.com"
            sx={{ width: 82, height: "auto", objectFit: "contain", display: "block" }}
          />
          <MDButton variant="text" sx={{ color: COLORS.ink, fontWeight: 900, minWidth: "auto", px: 0.5 }} onClick={() => navigate("/m")}>
            {t("Home")}
          </MDButton>
        </MDBox>

        <MDTypography
          variant="h1"
          sx={{
            color: COLORS.ink,
            fontWeight: 900,
            fontSize: "clamp(2rem, 8vw, 2.7rem)",
            lineHeight: 1.02,
            letterSpacing: 0,
            mb: 2.25,
          }}
        >
          {t("Sign in.")}
        </MDTypography>

        {error ? (
          <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: 1 }}>
            {error}
          </Alert>
        ) : null}

        <GridSocial onSocial={onSocial} loading={loading} />

        <MDBox component="form" onSubmit={onSubmit} noValidate sx={{ mt: 2.35 }}>
          <Stack spacing={0.75}>
            <TextField
              label={t("Email")}
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              fullWidth
            />
            <TextField
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={t("Toggle password visibility")} onClick={() => setShowPassword((v) => !v)} edge="end">
                      <Icon fontSize="small">{showPassword ? "visibility_off" : "visibility"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <MDButton
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{
                bgcolor: COLORS.red,
                color: "#fff",
                borderRadius: 1,
                py: 1.42,
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": { bgcolor: "#af1f1a", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "rgba(11,17,24,0.12)", color: "rgba(11,17,24,0.38)" },
              }}
            >
              {loading ? t("Signing in...") : t("Sign in")}
            </MDButton>
          </Stack>
        </MDBox>

        <MDBox sx={{ mt: 3, pt: 2, borderTop: `1px solid ${COLORS.line}` }}>
          <MDButton
            fullWidth
            variant="outlined"
            sx={{
              color: COLORS.ink,
              borderColor: "rgba(11,17,24,0.12)",
              borderRadius: 1,
              py: 1.28,
              fontWeight: 900,
            }}
            onClick={() => navigate(registerPath)}
          >
          {t("Create an account instead")}
          </MDButton>
        </MDBox>
      </Container>
    </Box>
  );
}

function GridSocial({ onSocial, loading }) {
  const buttonSx = {
    borderColor: COLORS.line,
    color: COLORS.ink,
    borderRadius: 1,
    py: 1.25,
    bgcolor: "#fff",
    minHeight: 50,
    boxShadow: "none",
    "&:hover": { borderColor: "rgba(207,36,31,0.35)", bgcolor: "#fff" },
  };
  return (
    <Stack direction="row" spacing={0.75}>
      <MDButton fullWidth variant="outlined" sx={buttonSx} disabled={loading} onClick={() => onSocial("google")}>
        <GoogleIcon fontSize="small" />
      </MDButton>
      <MDButton fullWidth variant="outlined" sx={buttonSx} disabled={loading} onClick={() => onSocial("apple")}>
        <AppleIcon fontSize="small" />
      </MDButton>
      <MDButton fullWidth variant="outlined" sx={buttonSx} disabled={loading} onClick={() => onSocial("microsoft")}>
        <MicrosoftMark />
      </MDButton>
    </Stack>
  );
}

GridSocial.propTypes = {
  onSocial: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};
